import React, { useCallback, useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useDocumentState } from '../../../../../contexts/DocumentContext';
import { Card } from './components/card';
import { ActivityCardEnum } from '../../../../card/ActivitiesCardConstants';
import { DocumentActivityTypesEnum } from './documentActivityDropdown';
import {
  ActivitiesNavigation,
  ActivityPropsType,
  DocumentActivityActions,
  DocumentActivityTypeEnum,
} from './components/constants';
import { useDocumentActivities, DocumentActivity } from '../../../../../shared/hooks/useDocumentActivities';
import { getCommentsForLeaseDocumentSection } from '../../../../../utils/api/get-comments-for-document-section/get-comments-for-document-section';
import { useApolloClient } from '@apollo/client';
import { getDocumentSuggestionById } from '../../../../../utils/api/get-document-suggestion-by-id/get-document-suggestion-by-id';
import { ReactComponent as ChevronRightIcon } from '../../../../../shared/icons/chevron-right.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../../../shared/icons/chevron-left.svg';
import styles from './components/styles.module.scss';
import { RenderDocumentActivityData } from './helpers';
import { FormattedMessage } from 'react-intl';
import { TitleFormattedMessage } from '../../../../titleFormattedMessage';

type Props = {
  currentDocumentActivityType: DocumentActivityTypesEnum;
  shouldAllActivitiesBeShown: boolean;
};

const MAX_ACTIVITIES_NUMBER_TO_DISPLAY = 99;

// Internal component that uses the local DB activities hook
const DocumentActivitiesFromDB = ({
  documentId,
  renderActivityData,
  renderPlaceholder,
  currentDocumentActivityType,
  shouldAllActivitiesBeShown,
  onFilteredCountChange,
}: {
  documentId: string;
  renderActivityData: (activityProps: ActivityPropsType) => React.ReactElement;
  renderPlaceholder: () => React.ReactElement;
  currentDocumentActivityType: string;
  shouldAllActivitiesBeShown: boolean;
  onFilteredCountChange?: (count: number) => void;
}) => {
  const { activities, loading, error, hasMore } = useDocumentActivities({
    documentId,
    limit: 100,
  });

  // Helper function to check if an activity should be displayed based on filters
  const shouldShowActivity = useCallback(
    (activity: DocumentActivity, activityProps: ActivityPropsType): boolean => {
      const matchesTypeFilter =
        currentDocumentActivityType === DocumentActivityTypesEnum.all ||
        Boolean(
          activity.description?.includes(
            currentDocumentActivityType === DocumentActivityTypesEnum.comments
              ? DocumentActivityTypeEnum.comment
              : DocumentActivityTypeEnum.suggestion
          )
        );

      const matchesActiveFilter = shouldAllActivitiesBeShown ? true : !activityProps.inactive;

      return matchesTypeFilter && matchesActiveFilter;
    },
    [currentDocumentActivityType, shouldAllActivitiesBeShown]
  );

  // Filter activities that will actually be rendered
  const activitiesToRender = React.useMemo(() => {
    const result: Array<{ activity: DocumentActivity; activityProps: ActivityPropsType }> = [];

    activities.forEach((activity: DocumentActivity) => {
      const activityProps: ActivityPropsType = {
        id: activity.id,
        description: activity.description || '',
        time: activity.time,
        actor_data: activity.actor_data || {
          full_name: 'System',
          email: '',
        },
        verb: activity.verb,
        extra: activity.extra || {
          external_id: '',
          section_name: '',
        },
        object_data: activity.object_data || {
          object_id: '',
        },
        inactive: activity.inactive,
      };

      if (shouldShowActivity(activity, activityProps)) {
        result.push({ activity, activityProps });
      }
    });

    return result;
  }, [activities, shouldShowActivity]);

  // Notify parent component of filtered count
  useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(activitiesToRender.length);
    }
  }, [activitiesToRender.length, onFilteredCountChange]);

  if (loading) {
    return <div className="text-center p-4">Loading activities...</div>;
  }

  if (error) {
    console.error('Error loading document activities:', error);
    return <div className="text-center p-4">Error loading activities</div>;
  }

  if (!activities.length || activitiesToRender.length === 0) {
    return renderPlaceholder();
  }

  return (
    <>
      {activitiesToRender.map(({ activity, activityProps }) => (
        <div key={activity.id}>{renderActivityData(activityProps)}</div>
      ))}

      {/* Activity count and pagination info */}
      <div className="text-center text-muted small p-2">
        Showing {activitiesToRender.length} activities{hasMore ? ' (more available)' : ''}
      </div>
    </>
  );
};

const DocumentActivitySidebar = ({ currentDocumentActivityType, shouldAllActivitiesBeShown }: Props) => {
  const { state: { id: documentId } } = useDocumentState();
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>();
  const [hoveredActivityId, setHoveredActivityId] = useState<string | null>();
  const [isActivityDataLoading, setIsActivityDataLoading] = useState(false);
  const [filteredActivitiesCount, setFilteredActivitiesCount] = useState(0);
  const [selectedActivityState, setSelectedActivityState] = useState<string | null>(null);
  const apolloClient = useApolloClient();

  const handleFilteredCountChange = useCallback((count: number) => {
    setFilteredActivitiesCount(count);
  }, []);

  const handleActivityNavigation = (action: ActivitiesNavigation) => {
    const currentActivityButton = document.getElementById(`button--${selectedActivityId}`);
    const startActivityButton = document.querySelector('[name="document-activity-item-button"]') as HTMLElement;

    let targetButton: HTMLElement | null = null;

    if (!currentActivityButton) {
      // No selection yet — start at first activity
      targetButton = startActivityButton;
    } else {
      // Buttons are wrapped in divs, so navigate via the wrapper's siblings
      const wrapper = currentActivityButton.parentElement;
      const siblingWrapper = action === ActivitiesNavigation.NEXT
        ? wrapper?.nextElementSibling
        : wrapper?.previousElementSibling;
      targetButton = siblingWrapper?.querySelector('[name="document-activity-item-button"]') as HTMLElement | null;
    }

    if (!targetButton) return;

    const targetId = targetButton.id?.split('--')[1];

    targetButton.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    targetButton.click();

    setHoveredActivityId(targetId);
  };

  const handleActivityClick = useCallback(
    async ({
      activityProps,
      sectionId,
      activityDatabaseId,
    }: {
      activityProps: ActivityPropsType;
      sectionId: string;
      activityDatabaseId: string;
    }) => {
      setIsActivityDataLoading(true);
      setSelectedActivityState(null);
      setSelectedActivityId(activityProps.id);

      // Annotation-balloon focusing was a CKEditor nicety on top of the DOM
      // `scrollIntoView` below, which is what actually moves the view now.
      const annotationThreads: any[] = [];

      const isCommentActive = window.document.querySelectorAll(`[data-comment="${sectionId}"]`);
      const isSuggestionActive = window.document.querySelectorAll(
        `[data-suggestion="${sectionId || activityProps?.extra?.external_id}"]`
      );

      const checkIfReplyWasDeleted = async (internalId: string, sectionId: string, documentId: string) => {
        return await getCommentsForLeaseDocumentSection(
          documentId!.toString(),
          sectionId.toString()
        )(apolloClient).then((data: any) => {
          if (data.comments[0].id === internalId.toString()) {
            return data.isResolved;
          } else {
            const relevantComment = data.comments?.find((reply: { id: string }) => {
              return reply.id === internalId.toString();
            });
            return relevantComment?.isResolved;
          }
        });
      };

      if (activityProps.description === DocumentActivityActions.NEW_SUGGESTION) {
        const suggestionStatus = await getDocumentSuggestionById(activityProps?.extra?.external_id || sectionId)(
          apolloClient
        ).then((data: any) => {
          data.status !== 'pending' && setSelectedActivityState(`This suggestion has been ${data.status}`);
          return data.status;
        });
        if (suggestionStatus !== 'pending') {
          setIsActivityDataLoading(false);
          return;
        }
      }

      if (activityProps.description === DocumentActivityActions.NEW_COMMENT) {
        if (!isCommentActive.length) {
          setSelectedActivityState('This comment has been deleted');
          setIsActivityDataLoading(false);
          return;
        }
      }

      if (activityProps.description === DocumentActivityActions.NEW_SUGGESTION_REPLY) {
        const suggestionStatus = await getDocumentSuggestionById(activityProps?.extra?.external_id || sectionId)(
          apolloClient
        ).then((data: any) => {
          data.status !== 'pending' && setSelectedActivityState(`This suggestion has been ${data.status}`);
          return data.status;
        });

        if (suggestionStatus !== 'pending') {
          setIsActivityDataLoading(false);
          return;
        }

        const isReplyDeleted = await checkIfReplyWasDeleted(
          activityDatabaseId,
          activityProps?.extra?.external_id || sectionId,
          documentId as string
        );

        if (isReplyDeleted) {
          setIsActivityDataLoading(false);
          return setSelectedActivityState('This suggestion reply has been deleted');
        }
      }

      if (activityProps.description === DocumentActivityActions.NEW_COMMENT_REPLY) {
        if (!isCommentActive.length) {
          setIsActivityDataLoading(false);
          return setSelectedActivityState('This comment has been deleted');
        }
        const isReplyDeleted = await checkIfReplyWasDeleted(
          activityDatabaseId,
          activityProps?.extra?.external_id || sectionId,
          documentId as string
        );

        if (isReplyDeleted) {
          setIsActivityDataLoading(false);
          return setSelectedActivityState('This comment reply has been deleted');
        }
      }

      const selectedAnnotation = annotationThreads.find((currentAnnotation: any) => {
        return currentAnnotation?.id === (sectionId || activityProps?.extra?.external_id);
      });

      isCommentActive[0]?.scrollIntoView();
      isSuggestionActive[0]?.scrollIntoView();

      // Focus on the selected annotation using the v45 API
      if (selectedAnnotation) {
        selectedAnnotation.focus();
      }

      setIsActivityDataLoading(false);

      setTimeout(() => {
        // Blur the annotation after a short delay
        if (selectedAnnotation) {
          selectedAnnotation.blur();
        }
      }, 100);
    },
    [apolloClient, documentId]
  );

  const renderActivityData = useCallback(
    (activityProps: ActivityPropsType) => {
      return (
        <RenderDocumentActivityData
          activityProps={activityProps}
          currentDocumentActivityType={currentDocumentActivityType}
          shouldAllActivitiesBeShown={shouldAllActivitiesBeShown}
          selectedActivityId={selectedActivityId}
          selectedActivityState={selectedActivityState}
          hoveredActivityId={hoveredActivityId}
          isActivityDataLoading={isActivityDataLoading}
          handleActivityClick={handleActivityClick}
          setHoveredActivityId={setHoveredActivityId}
        />
      );
    },
    [
      currentDocumentActivityType,
      shouldAllActivitiesBeShown,
      handleActivityClick,
      hoveredActivityId,
      isActivityDataLoading,
      selectedActivityId,
      selectedActivityState,
    ]
  );

  const renderPlaceholder = () => (
    <div className="text-center pl-4 pr-4">
      <FormattedMessage id="activity.no-data-message" defaultMessage="No comments or suggestions" />
    </div>
  );

  // Early return if no document ID
  if (!documentId) {
    return (
      <Card isBlue isFullHeight width={ActivityCardEnum.BOOTSTRAP_WIDTH}>
        <div className="text-center pl-4 pr-4">
          <FormattedMessage id="activity.loading" defaultMessage="Loading activities..." />
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className={styles.navigationContainer}>
        <Button onClick={() => handleActivityNavigation(ActivitiesNavigation.PREV)}>
          <span className="pr-1">
            <ChevronLeftIcon height={8} />
          </span>
          <TitleFormattedMessage id="cta.prev" defaultMessage="Prev" />
        </Button>
        <span>
          {filteredActivitiesCount < MAX_ACTIVITIES_NUMBER_TO_DISPLAY
            ? filteredActivitiesCount
            : `${MAX_ACTIVITIES_NUMBER_TO_DISPLAY}+`}{' '}
          items
        </span>
        <Button onClick={() => handleActivityNavigation(ActivitiesNavigation.NEXT)}>
          <TitleFormattedMessage id="cta.next" defaultMessage="Next" />
          <span className="pl-1">
            <ChevronRightIcon height={8} />
          </span>
        </Button>
      </div>
      <Card isBlue isFullHeight width={ActivityCardEnum.BOOTSTRAP_WIDTH}>
        <DocumentActivitiesFromDB
          documentId={documentId as string}
          renderActivityData={renderActivityData}
          renderPlaceholder={renderPlaceholder}
          currentDocumentActivityType={currentDocumentActivityType}
          shouldAllActivitiesBeShown={shouldAllActivitiesBeShown}
          onFilteredCountChange={handleFilteredCountChange}
        />
      </Card>
    </>
  );
};

export default DocumentActivitySidebar;
